import type { ChecksumAddress } from './address';
import {
  isSanctionedAddress,
  normalizeAddressForComparison,
} from './sanctionedAddresses';

export const SANCTIONED_ADDRESS_INTERACTION_MESSAGE =
  "This address is part of the U.S. Office of Foreign Assets Control's Specially Designated Nationals And Blocked Persons List. You cannot interact with it.";

export const SANCTIONED_TRANSACTION_INTERACTION_MESSAGE =
  "This transaction contains an address that is part of the U.S. Office of Foreign Assets Control's Specially Designated Nationals And Blocked Persons List. You cannot interact with it.";

/**
 * Two-letter ISO country codes for countries that are sanctioned by the US.
 *
 * This mirrors the self-custody implementation so region blocking stays
 * aligned across products.
 */
export const SANCTIONED_COUNTRY_CODES = new Set([
  'AF',
  'BY',
  'MM',
  'CF',
  'CN',
  'CU',
  'CD',
  'ET',
  'IR',
  'IQ',
  'LB',
  'LY',
  'ML',
  'NI',
  'KP',
  'RU',
  'SO',
  'SS',
  'SD',
  'SY',
  'UA',
  'VE',
  'YE',
  'ZW',
]);

const EVM_ADDRESS_REGEX = /0x[a-fA-F0-9]{40}/g;

function collectCandidateAddressStrings(
  value: unknown,
  candidates: Set<string>,
  seen: Set<object>
): void {
  if (typeof value === 'string') {
    const matches = value.match(EVM_ADDRESS_REGEX) ?? [];
    matches.forEach((match) => candidates.add(match));
    return;
  }

  if (!value || typeof value !== 'object') {
    return;
  }

  if (seen.has(value)) {
    return;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) => {
      collectCandidateAddressStrings(item, candidates, seen);
    });
    return;
  }

  Object.values(value as Record<string, unknown>).forEach((item) => {
    collectCandidateAddressStrings(item, candidates, seen);
  });
}

/**
 * Scans a nested value for EVM addresses and returns only the checksum addresses
 * that appear in the sanctioned set.
 */
export function getSanctionedAddressesFromValue(
  value: unknown
): ChecksumAddress[] {
  const candidateAddresses = new Set<string>();
  collectCandidateAddressStrings(value, candidateAddresses, new Set());

  return [...candidateAddresses].flatMap((candidateAddress) => {
    const normalizedAddress = normalizeAddressForComparison(candidateAddress);
    if (!normalizedAddress || !isSanctionedAddress(normalizedAddress)) {
      return [];
    }

    return [normalizedAddress as ChecksumAddress];
  });
}
